import { useEffect, useRef } from "react";
import { isTV } from "../utils/platform";
import {
  exitFullscreenApp,
  getFullscreenElement,
  requestFullscreenApp,
} from "../models/screen";

const STORAGE_KEY = "hasExitedFullscreen";
const isIOSPhone = /iPhone|iPod/.test(navigator.userAgent);

export const useFullscreenOnFirstTouch = (enabled = true) => {
  const isListenerAttached = useRef(false);
  const wasFullscreen = useRef(false);
  const prevEnabled = useRef(enabled);

  // Handle toggle ON/OFF from settings
  useEffect(() => {
    if (isTV || isIOSPhone) return;

    if (enabled && !prevEnabled.current) {
      if (!getFullscreenElement()) {
        requestFullscreenApp();
      }
    }

    if (!enabled && prevEnabled.current) {
      if (getFullscreenElement()) {
        exitFullscreenApp();
      }
    }

    prevEnabled.current = enabled;
  }, [enabled]);

  useEffect(() => {
    if (!enabled || isTV || isIOSPhone) return;
    if (document.fullscreenElement) return;

    const removeListener = () => {
      if (isListenerAttached.current) {
        document.removeEventListener("click", handleTouch, { capture: true });
        isListenerAttached.current = false;
      }
    };

    const handleTouch = async (e: Event) => {
      if (!e.isTrusted) return;
      // Don't trigger fullscreen if the click is inside a dialog,
      // otherwise popup will be hide behind fullscreen and user can't interact with it to close
      if ((e.target as Element)?.closest("dialog[open]")) return;
      e.stopPropagation();
      removeListener();
      try {
        await requestFullscreenApp();
      } catch (error) {
        console.error("[Fullscreen] failed:", error);
      }
    };

    const handleFullscreenChange = () => {
      if (document.fullscreenElement) {
        // Entered fullscreen
        wasFullscreen.current = true;
      } else if (wasFullscreen.current) {
        // Exited fullscreen (from fullscreen state)
        sessionStorage.setItem(STORAGE_KEY, "true");
        removeListener();
      }
    };

    // Capture phase so we intercept before any UI element processes the click
    document.addEventListener("click", handleTouch, { capture: true });
    isListenerAttached.current = true;

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);

    return () => {
      removeListener();
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange,
      );
    };
  }, [enabled]);
};
